import React, { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import LanguageToggle from '../components/LanguageToggle'
import { supabase } from '../lib/supabase'
import interactiveBusinessLogo from '../Logo/InteractiveBusinessLogo.svg'

const SESSION_STATUS = {
  CHECKING: 'checking',
  READY: 'ready',
  INVALID: 'invalid',
}

const UpdatePasswordPage = () => {
  const { t } = useTranslation('auth')
  const navigate = useNavigate()
  const [sessionStatus, setSessionStatus] = useState(SESSION_STATUS.CHECKING)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const recoveryWaitTimeoutRef = useRef(null)
  const redirectTimeoutRef = useRef(null)

  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''))
    const searchParams = new URLSearchParams(window.location.search)
    const hasRecoveryHint =
      hashParams.get('type') === 'recovery' || searchParams.get('type') === 'recovery'

    const clearRecoveryTimeout = () => {
      if (recoveryWaitTimeoutRef.current) {
        clearTimeout(recoveryWaitTimeoutRef.current)
        recoveryWaitTimeoutRef.current = null
      }
    }

    const clearRedirectTimeout = () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current)
        redirectTimeoutRef.current = null
      }
    }

    const markReady = () => {
      clearRecoveryTimeout()
      setSessionStatus(SESSION_STATUS.READY)
    }

    const markInvalid = () => {
      clearRecoveryTimeout()
      setSessionStatus(SESSION_STATUS.INVALID)
    }

    let isMounted = true

    const initializeSession = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession()

        if (!isMounted) return

        if (session?.user) {
          markReady()
          return
        }

        if (!hasRecoveryHint) {
          markInvalid()
          return
        }

        recoveryWaitTimeoutRef.current = setTimeout(() => {
          if (!isMounted) return
          markInvalid()
        }, 3500)
      } catch (sessionError) {
        console.error('Unable to verify recovery session:', sessionError)
        if (isMounted) {
          markInvalid()
        }
      }
    }

    initializeSession()

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted) return

      if (
        (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') &&
        session?.user
      ) {
        markReady()
      }
    })

    return () => {
      isMounted = false
      subscription.unsubscribe()
      clearRecoveryTimeout()
      clearRedirectTimeout()
    }
  }, [])

  const validatePassword = () => {
    if (!newPassword) {
      return t('validation.passwordRequired')
    }

    if (newPassword.length < 6) {
      return t('validation.passwordMinLength')
    }

    if (newPassword !== confirmPassword) {
      return t('updatePasswordPage.passwordMismatch')
    }

    return ''
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    setError('')

    const validationError = validatePassword()
    if (validationError) {
      setError(validationError)
      return
    }

    setLoading(true)
    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      })

      if (updateError) {
        throw updateError
      }

      setSuccessMessage(t('updatePasswordPage.successMessage'))
      setNewPassword('')
      setConfirmPassword('')

      redirectTimeoutRef.current = setTimeout(async () => {
        await supabase.auth.signOut().catch((signOutError) => {
          console.error('Error signing out after password reset:', signOutError)
        })

        navigate('/login', {
          replace: true,
        })
      }, 1500)
    } catch (saveError) {
      console.error('Failed to save new password:', saveError)
      setError(t('updatePasswordPage.saveError'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-3 flex flex-col justify-center sm:flex sm:items-center sm:justify-center sm:px-6 sm:py-8">
      <div className="mx-auto flex w-full max-w-xl flex-col sm:max-w-md">
        <div className="mb-5 flex items-start justify-between gap-3 sm:hidden">
          <img src={interactiveBusinessLogo} alt="Interactive Business logo" className="h-10 w-auto" />

          <div className="flex items-center gap-2">
            <Link
              to="/about"
              className="text-sm font-medium text-gray-700 transition-colors hover:text-[#FF1B54]"
            >
              About
            </Link>
            <LanguageToggle />
          </div>
        </div>

        <div className="hidden sm:block absolute top-6 left-6">
          <img src={interactiveBusinessLogo} alt="Interactive Business logo" className="h-12 w-auto" />
        </div>

        <div className="hidden sm:flex absolute top-4 right-4 items-center gap-4">
          <Link
            to="/about"
            className="text-base font-medium text-gray-700 transition-colors hover:text-[#FF1B54]"
          >
            About
          </Link>
          <LanguageToggle />
        </div>

        <div className="w-full space-y-5 sm:space-y-10">
          <div>
            <h2 className="text-center text-3xl font-extrabold leading-tight text-gray-900 sm:mt-6 sm:text-4xl">
              <span className="text-[#FF1B54]">Interactive </span>
              <span className="text-gray-900">Business</span>
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600 sm:mt-3 sm:text-base">
              {t('updatePasswordPage.subtitle')}
            </p>
          </div>

          <div className="rounded-lg bg-white p-6 shadow sm:p-8">
            <h1 className="text-xl font-semibold text-gray-900">{t('updatePasswordPage.title')}</h1>
            <p className="mt-2 text-sm text-gray-600">{t('updatePasswordPage.description')}</p>

            {sessionStatus === SESSION_STATUS.CHECKING && (
              <div className="mt-6 flex items-center justify-center rounded border border-gray-200 bg-gray-50 px-4 py-6">
                <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-[#FF1B54]" />
              </div>
            )}

            {sessionStatus === SESSION_STATUS.INVALID && (
              <div className="mt-6 rounded border border-red-200 bg-red-50 px-4 py-3 text-red-700">
                <p className="font-medium">{t('updatePasswordPage.invalidLinkTitle')}</p>
                <p className="mt-1 text-sm">{t('updatePasswordPage.invalidLinkDescription')}</p>
              </div>
            )}

            {sessionStatus === SESSION_STATUS.READY && (
              <form onSubmit={handleSubmit} className="mt-6 space-y-6">
                {error && (
                  <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-red-700">
                    {error}
                  </div>
                )}

                {successMessage && (
                  <div className="rounded border border-green-200 bg-green-50 px-4 py-3 text-green-700">
                    {successMessage}
                  </div>
                )}

                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                    {t('updatePasswordPage.newPasswordLabel')}
                  </label>
                  <input
                    id="newPassword"
                    name="newPassword"
                    type="password"
                    required
                    value={newPassword}
                    onChange={(event) => setNewPassword(event.target.value)}
                    placeholder={t('updatePasswordPage.newPasswordPlaceholder')}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring-[#FF1B54] focus:border-[#FF1B54]"
                  />
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                    {t('updatePasswordPage.confirmPasswordLabel')}
                  </label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    required
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    placeholder={t('updatePasswordPage.confirmPasswordPlaceholder')}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring-[#FF1B54] focus:border-[#FF1B54]"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="flex w-full justify-center rounded-md border border-transparent bg-[#FF1B54] py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-[#E1174C] focus:outline-none focus:ring-2 focus:ring-[#FF1B54] focus:ring-offset-2 disabled:opacity-50"
                >
                  {loading ? t('updatePasswordPage.saving') : t('updatePasswordPage.submitButton')}
                </button>
              </form>
            )}

            <div className="mt-6 text-center">
              <Link to="/login" className="text-sm text-[#FF1B54] hover:text-[#E1174C]">
                {t('updatePasswordPage.backToLogin')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UpdatePasswordPage
